import { Stack, Typography, Box, styled, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useQueryParams } from 'src/hooks/useQueryParam';
import { FORMAT_DATE_TIME } from 'src/utils/constants/config';
import { TypeDataPackage } from 'src/utils/constants/enum';
import { LOGO_COMPANY_DEFAULT_URL } from 'src/utils/constants/imagesUrl';
import { getCountryByCountryCode } from 'src/utils/helpers/stringHelpers';

const StyledBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  marginTop: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  background: 'linear-gradient(180deg, #7551FF 0%, #3311DB 100%)',
  boxShadow: '0px 0px 50px 0px rgba(112, 114, 176, 0.08)',
}));

const TypographyWhite = styled(Typography)(({ theme }) => ({
  color: theme.palette.common.white,
  overflow: 'hidden',
}));

const Information = () => {
  const theme = useTheme();
  const {
    type_package = TypeDataPackage.top_20_export_company_in_country,
    trade_data_from = '',
    trade_data_to = '',
    country: country_code,
    import_country: import_country_code,
    export_country: export_country_code,
    is_worldwide,
  } = useQueryParams();

  const country = getCountryByCountryCode(String(country_code));
  const importCountry = getCountryByCountryCode(String(import_country_code));
  const exportCountry = getCountryByCountryCode(String(export_country_code));

  const isWorldwide = useMemo(() => is_worldwide === 'true', [is_worldwide]);

  const renderTitle = () => {
    switch (type_package) {
      case TypeDataPackage.top_20_export_company_in_country:
        return `Top 20 Exporting Companies ${isWorldwide ? 'All Over The World' : `In ${country.label}`}`;
      case TypeDataPackage.top_20_import_company_in_country:
        return `Top 20 Importing Companies ${isWorldwide ? 'All Over The World' : `In ${country.label}`}`;
      case TypeDataPackage.top_20_export_product_in_country:
        return `Top 20 Most Exporting Products ${isWorldwide ? 'All Over The World' : `In ${country.label}`}`;
      case TypeDataPackage.top_20_import_product_in_country:
        return `Top 20 Most Imported Products ${isWorldwide ? 'All Over The World' : `In ${country.label}`}`;
      default:
        return `Top 20 Trade Balance Analysis Between ${importCountry.label} and ${exportCountry.label}`;
    }
  };

  const renderDescription = () => {
    switch (type_package) {
      case TypeDataPackage.top_20_export_company_in_country:
      case TypeDataPackage.top_20_import_company_in_country:
        return 'Discover valuable insights with our comprehensive list, showcasing the top performers and their key metrics. This table provides a clear overview, enabling informed decision-making and strategic analysis for businesses and stakeholders.';
      case TypeDataPackage.top_20_export_product_in_country:
      case TypeDataPackage.top_20_import_product_in_country:
        return 'Discover valuable insights with our comprehensive list, showcasing the top performers and their key metrics. This table provides a clear overview, enabling informed decision-making and strategic analysis for businesses and stakeholders.';
      default:
        return `Analyze the trade balance from the ${importCountry.label} perspective, with ${exportCountry.label} as the main comparison partner.`;
    }
  };

  return (
    <StyledBox>
      <Stack alignItems="center" direction="row" mb={1}>
        <Box
          sx={{
            backgroundColor: theme.palette.background.default,
            borderRadius: theme.shape.borderRadius / 4,
            p: 0.5,
          }}
          width={48}
          mr={1}
          src={LOGO_COMPANY_DEFAULT_URL}
          component="img"
          onError={({ currentTarget }) => {
            currentTarget.onerror = null; // prevents looping
            currentTarget.src = LOGO_COMPANY_DEFAULT_URL;
          }}
        />
        <Stack direction="column">
          <TypographyWhite align="left" variant="h5" gutterBottom>
            {renderTitle()}
          </TypographyWhite>
          <TypographyWhite align="left" variant="subtitle2" gutterBottom>
            {dayjs(String(trade_data_from) || null, FORMAT_DATE_TIME.MONTH_YEAR)
              .utc(true)
              .local()
              .format('MMM YYYY')}
            {' - '}
            {dayjs(String(trade_data_to) || null, FORMAT_DATE_TIME.MONTH_YEAR)
              .utc(true)
              .local()
              .format('MMM YYYY')}
          </TypographyWhite>
        </Stack>
      </Stack>
      <TypographyWhite align="left" variant="body2">
        {renderDescription()}
      </TypographyWhite>
    </StyledBox>
  );
};

export default Information;
